import { Col, Container, Nav, Row, Spinner } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProfileInfo from './ProfileInfo'
import ProfileSection from './ProfileSection'
import { useNavigate, useParams } from 'react-router-dom'
import apiCandidates from '../../services/api/localCandidates'
import { ProfileSectionConfirmationModal } from './ProfileSectionModal'
import { Typography } from '../styled'
import theme from '../../constants/theme'
import {
  DataRevisionContainer,
  HeaderTitleBold,
  HighlightedHeading,
  PersonalInfoLink,
  PersonalInfoTextStyle,
  ProfileSectionTitleTextStyle,
  VibrantText,
} from './styles'
import CheckOne from '../../assets/svgs/checkOne'

const ControlledNav = () => {
  const { t } = useTranslation()
  const { token } = useParams()
  const navigate = useNavigate()

  const activeTabs = [
    {
      key: 'personal-info',
      title: t('profile.personalInfo'),
    },
    {
      key: 'experience',
      title: t('profile.experience'),
    },
    {
      key: 'education',
      title: t('profile.education'),
    },
  ]

  const [activeKey, setActiveKey] = useState(activeTabs[0].key)

  const [positionInfo, setPositionInfo] = useState({})
  const [profileInfo, setProfileInfo] = useState({})
  const [experiences, setExperiences] = useState([])
  const [educations, setEducations] = useState([])

  const STATUS = {
    LOADING: 'loading',
    LOADED: 'loaded',
    ERROR: 'error',
    NOT_PARSED: 'not-parsed',
    USED: 'used',
    SUBMITTED: 'submitted',
  }

  const [currentStatus, setCurrentStatus] = useState(STATUS.LOADING)
  const [errorMessage, setErrorMessage] = useState('')

  const handleFinalSubmit = () => {
    apiCandidates
      .updateParsedProfileInfo(token, {
        profileInfo,
        experiences,
        educations,
      })
      .then(() => {
        setCurrentStatus(STATUS.SUBMITTED)
      })
  }

  const navLinks = [
    {
      key: activeTabs[0].key,
      title: activeTabs[0].title,
      child: (
        <ProfileInfo
          handleNextTab={() => {
            setActiveKey(activeTabs[1].key)
          }}
          defaultValues={profileInfo}
          updateProfileInfo={(data) => {
            setProfileInfo((prev) => ({ ...prev, ...data }))
            setActiveKey(activeTabs[1].key)
          }}
        />
      ),
    },
    {
      key: activeTabs[1].key,
      title: activeTabs[1].title,
      child: (
        <ProfileSection
          responseProfileSectionItems={experiences}
          handlePrevTab={() => {
            setActiveKey(activeTabs[0].key)
          }}
          handleNextTab={() => {
            setActiveKey(activeTabs[2].key)
          }}
          handleUpdate={(data) => {
            setExperiences([...data])
          }}
          objectKey="experience"
        />
      ),
    },
    {
      key: activeTabs[2].key,
      title: activeTabs[2].title,
      child: (
        <ProfileSection
          responseProfileSectionItems={educations}
          handlePrevTab={() => {
            setActiveKey(activeTabs[1].key)
          }}
          handleNextTab={handleFinalSubmit}
          isSubmit
          handleUpdate={(data) => {
            setEducations([...data])
          }}
          objectKey="education"
        />
      ),
    },
  ]

  const renderNavLinks = () =>
    navLinks.map(({ key, title }, index) => (
      <PersonalInfoLink
        className={activeKey === key ? 'active' : ''}
        key={key}
        isActive={activeKey === key}
      >
        <div className="d-flex flex-row justify-content-center align-self-center align-items-start w-100">
          <Col sm="auto">
            <VibrantText isActive={activeKey === key}>{index + 1}</VibrantText>
          </Col>
          <Col className="w-100 m-auto text-start ms-2 mt-2">
            <PersonalInfoTextStyle isActive={activeKey === key}>
              {title}
            </PersonalInfoTextStyle>
          </Col>
        </div>
      </PersonalInfoLink>
    ))

  const handleCurrentStatus = (res) => {
    setCurrentStatus(STATUS.ERROR)
    if (res.response?.status === 401)
      setErrorMessage(
        res.response?.data?.error_message ||
          t('customModal.applicationAlreadyUsedOrExpired'),
      )
    else if (res.response?.status === 403)
      setErrorMessage(t('customModal.applicationAlreadyUsed'))
    else if (res.response?.status === 404)
      setErrorMessage(t('customModal.yourApplicationDataNotFound'))
    else if (res.response?.status === 500)
      setErrorMessage(t('customModal.serverError'))
  }

  useEffect(() => {
    apiCandidates
      .getParsedProfileInfoFromToken(token)
      .then((res) => {
        setPositionInfo(res.positionInfo)
        setProfileInfo(res.personalInfo)
        setExperiences(res.experience)
        setEducations(res.education)
        setCurrentStatus(STATUS.LOADED)
      })
      .catch((error) => {
        handleCurrentStatus(error)
      })
  }, [token])

  const renderStatus = () => {
    switch (currentStatus) {
      case STATUS.LOADING:
        return (
          <Container>
            <Row
              className="d-flex justify-content-center align-items-center"
              style={{ height: '100vh' }}
            >
              <Spinner />
            </Row>
          </Container>
        )
      case STATUS.ERROR:
        return (
          <Container>
            <Row
              className="d-flex justify-content-center align-items-center text-center"
              style={{ height: '100vh' }}
            >
              <Typography size="16px" color={theme.inputField}>
                {errorMessage}
              </Typography>
            </Row>
          </Container>
        )
      default:
        return (
          <div style={{ minHeight: '100vh', paddingTop: '50px' }}>
            <ProfileSectionConfirmationModal
              show={currentStatus === STATUS.SUBMITTED}
              handleClose={() => {
                setCurrentStatus(STATUS.USED)
                navigate(0)
              }}
            >
              <div className="text-center my-4">
                <CheckOne width="80px" height="80px" />
              </div>
              <div className="text-center my-4">
                <Typography size="16px" color={theme.inputField}>
                  {t('customModal.applicationSubmitted')}
                </Typography>
              </div>
            </ProfileSectionConfirmationModal>

            <Container className="h-100">
              <Row
                className="d-flex justify-content-end align-items-end"
                style={{ height: '10%' }}
              >
                <ProfileSectionTitleTextStyle className="ms-2 text-capitalize">
                  <HeaderTitleBold>
                    {t('applicationFormFor')}:{' '}
                    <HighlightedHeading>
                      {positionInfo.positionTitle}
                    </HighlightedHeading>
                  </HeaderTitleBold>
                </ProfileSectionTitleTextStyle>
              </Row>
              <Row className="mb-2 bg-white">
                <Nav
                  className="nav-justified pe-0"
                  activeKey={activeKey}
                  onSelect={(selectedKey) => setActiveKey(selectedKey)}
                >
                  {renderNavLinks()}
                </Nav>
              </Row>

              <DataRevisionContainer
                className="h-75"
                style={{ minHeight: '75vh' }}
                backgroundColor="white"
              >
                {navLinks.filter((item) => item.key === activeKey)[0].child}
              </DataRevisionContainer>
            </Container>
          </div>
        )
    }
  }

  return renderStatus()
}

export default ControlledNav
